import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import {
  LayoutDashboard, Users, KanbanSquare, CheckCircle2, Receipt,
  ScrollText, Settings, Plus, ShieldCheck, FolderOpen, Landmark,
  TrendingUp, RefreshCw, FileSearch, Loader2, Sparkles, ArrowRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// A Finsola legacy id can be:
//   * "YY-MM-XXXX"          (docs - e.g. 17-04-0632)
//   * any digits ≥ 3        (partner finsola_id - e.g. 6843)
// This regex catches both, keyed on typed input.
const LEGACY_ID_PATTERN = /^(?:\d{1,2}-\d{1,2}-[a-z0-9]{2,6}|\d{3,10})$/i;

const COLL_LABEL = {
  clients: "Client / Counterparty",
  counterparties: "Counterparty",
  banks: "Bank",
  people: "Person",
  documents: "Document",
  banking_transactions: "Bank transaction",
  sales_invoices: "Sales invoice",
  finsola_events: "CRM event",
  finsola_kycs: "KYC anchor",
};

function resultTitle(coll, row) {
  return row.name || row.title || row.event_name || row.invoice_number
       || row.original_filename || row.entity_class || row.txn_id
       || row.doc_id || "(no title)";
}

function resultSubtitle(coll, row) {
  const bits = [];
  if (row.legacy_id) bits.push(`legacy=${row.legacy_id}`);
  if (row.finsola_id != null) bits.push(`finsola_id=${row.finsola_id}`);
  if (row.date) bits.push(String(row.date).slice(0, 10));
  if (row.category) bits.push(row.category);
  return bits.join(" · ");
}

export default function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [spotResults, setSpotResults] = useState(null); // {legacy_id, matches, count}
  const [spotLoading, setSpotLoading] = useState(false);
  const debounceRef = useRef(null);

  const go = (path) => { onOpenChange(false); navigate(path); };

  // Detect whether the typed query looks like a Finsola legacy id
  const isLegacyLike = useMemo(() => LEGACY_ID_PATTERN.test(query.trim()), [query]);

  // Reset state when palette closes
  useEffect(() => {
    if (!open) {
      setQuery("");
      setSpotResults(null);
      setSpotLoading(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
  }, [open]);

  // Debounced spot-check
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!isLegacyLike) { setSpotResults(null); return; }
    setSpotLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/migration/reconciliation/spot-check`,
          { params: { legacy_id: query.trim() } },
        );
        setSpotResults(data);
      } catch {
        setSpotResults(null);
      } finally {
        setSpotLoading(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, isLegacyLike]);

  const openSpotInRecon = (legacyId) => {
    go(`/app/migration/reconciliation?spot=${encodeURIComponent(legacyId)}`);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} title="Command palette" description="Search and navigate">
      <CommandInput
        data-testid="command-input"
        placeholder="Search or type a Finsola id (e.g. 17-04-0632 or 6843)…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {/* When user types a Finsola-like id, show live spot-check results FIRST */}
        {isLegacyLike && (
          <CommandGroup heading={
            <span className="flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-emerald-600" /> Legacy id lookup
              {spotLoading && <Loader2 className="w-3 h-3 animate-spin" />}
            </span>
          }>
            {!spotLoading && spotResults && spotResults.count === 0 && (
              <CommandItem disabled className="text-muted-foreground text-xs">
                No LyraOS row carries id "{query}"
              </CommandItem>
            )}
            {spotResults && spotResults.count > 0 && Object.entries(spotResults.matches).map(([coll, rows]) =>
              rows.map((row, i) => (
                <CommandItem
                  key={`${coll}-${i}`}
                  value={`legacy-${coll}-${i}-${resultTitle(coll, row)}`}
                  data-testid={`cmd-spot-${coll}-${i}`}
                  onSelect={() => openSpotInRecon(spotResults.legacy_id)}
                  className="items-start"
                >
                  <FileSearch className="w-4 h-4 mr-2 mt-0.5 flex-none text-emerald-600" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="truncate font-medium">
                        {resultTitle(coll, row).toString().slice(0, 60)}
                      </div>
                      <span className="text-[10px] font-mono text-muted-foreground flex-none">
                        {COLL_LABEL[coll] || coll}
                      </span>
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono truncate">
                      {resultSubtitle(coll, row)}
                    </div>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 ml-2 opacity-40 flex-none" />
                </CommandItem>
              ))
            )}
            {spotResults && spotResults.count > 0 && (
              <CommandItem
                data-testid="cmd-spot-open-full"
                onSelect={() => openSpotInRecon(spotResults.legacy_id)}
                className="text-emerald-700 dark:text-emerald-400"
              >
                <FileSearch className="w-4 h-4 mr-2" />
                Open full spot-check for "{spotResults.legacy_id}"
                <ArrowRight className="w-3.5 h-3.5 ml-auto" />
              </CommandItem>
            )}
          </CommandGroup>
        )}

        {isLegacyLike && <CommandSeparator />}
        <CommandEmpty>No results.</CommandEmpty>

        <CommandGroup heading="Navigate">
          <CommandItem data-testid="cmd-dashboard" onSelect={() => go("/app/dashboard")}><LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard</CommandItem>
          <CommandItem data-testid="cmd-clients" onSelect={() => go("/app/clients")}><Users className="w-4 h-4 mr-2" /> Counterparties</CommandItem>
          <CommandItem data-testid="cmd-requests" onSelect={() => go("/app/requests")}><KanbanSquare className="w-4 h-4 mr-2" /> Requests</CommandItem>
          <CommandItem data-testid="cmd-approvals" onSelect={() => go("/app/approvals")}><CheckCircle2 className="w-4 h-4 mr-2" /> Approvals</CommandItem>
          <CommandItem data-testid="cmd-documents" onSelect={() => go("/app/documents")}><FolderOpen className="w-4 h-4 mr-2" /> Documents</CommandItem>
          <CommandItem data-testid="cmd-invoices" onSelect={() => go("/app/invoices")}><Receipt className="w-4 h-4 mr-2" /> Invoices</CommandItem>
          <CommandItem data-testid="cmd-banking" onSelect={() => go("/app/banking")}><Landmark className="w-4 h-4 mr-2" /> Banking</CommandItem>
          <CommandItem data-testid="cmd-lending" onSelect={() => go("/app/lending")}><TrendingUp className="w-4 h-4 mr-2" /> Lending</CommandItem>
          <CommandItem data-testid="cmd-fx" onSelect={() => go("/app/fx")}><RefreshCw className="w-4 h-4 mr-2" /> FX Rates</CommandItem>
          <CommandItem data-testid="cmd-compliance" onSelect={() => go("/app/compliance")}><ShieldCheck className="w-4 h-4 mr-2" /> Compliance</CommandItem>
          <CommandItem data-testid="cmd-reports" onSelect={() => go("/app/reports")}><Receipt className="w-4 h-4 mr-2" /> Reports</CommandItem>
          <CommandItem data-testid="cmd-audit" onSelect={() => go("/app/audit")}><ScrollText className="w-4 h-4 mr-2" /> Audit Log</CommandItem>
          <CommandItem data-testid="cmd-admin" onSelect={() => go("/app/admin")}><Settings className="w-4 h-4 mr-2" /> Admin</CommandItem>
          <CommandItem data-testid="cmd-migration" onSelect={() => go("/app/migration")}><Sparkles className="w-4 h-4 mr-2" /> Migration wizard</CommandItem>
          <CommandItem data-testid="cmd-reconciliation" onSelect={() => go("/app/migration/reconciliation")}><FileSearch className="w-4 h-4 mr-2" /> Migration reconciliation</CommandItem>
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Quick actions">
          <CommandItem onSelect={() => go("/app/clients?new=1")}><Plus className="w-4 h-4 mr-2" /> New counterparty</CommandItem>
          <CommandItem onSelect={() => go("/app/requests?new=1")}><Plus className="w-4 h-4 mr-2" /> New request</CommandItem>
          <CommandItem onSelect={() => go("/app/mfa")}><ShieldCheck className="w-4 h-4 mr-2" /> Manage MFA</CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
